/*
  Motureo: kafelek „Zamów zestaw w jednej przesyłce” (blocks/motureo-bundle.liquid).
  - Pola wyboru przy dodatkach: suma, „cena za N produktów” i zdanie o dostawie liczą się na bieżąco (kwoty przez
    formatMoney z assets/money-formatting.js, ten sam format co filtr money; teksty z JSON w kafelku).
  - „Edytuj zestaw” odsłania kolejne dodatki (bez zaznaczenia).
  - Przycisk dodaje produkt z karty i zaznaczone dodatki jednym zapytaniem do koszyka (/cart/add z listą items,
    jak „#processBatchAddToCart” w assets/product-form.js) i wysyła to samo zdarzenie co zwykłe dodanie
    (CartLinesUpdateEvent ze standardowych zdarzeń sklepu): szuflada koszyka się otwiera, licznik i lista
    pozycji odświeżają się. Błąd (np. pozycja właśnie się wyprzedała) — komunikat w kafelku i CartErrorEvent.
  Zdanie o darmowej dostawie tylko, gdy serwer oznaczył kraj stawki (data-kraj-dostawy = true). Liczy się od koszyka
  + zestawu (data-koszyk = wartość koszyka przy renderowaniu, potem odświeżana po każdej zmianie koszyka), tak samo
  jak w blocks/motureo-bundle.liquid (przegląd Opusa r1, W4).
*/

import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const routes = () => /** @type {any} */ (globalThis).Theme?.routes || {};

/** Aktualny koszyk jak w product-form.js: przez <cart-items-component>, a bez niego z /cart.json. */
async function pobierzKoszyk() {
  const lista = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (lista) {
    await customElements.whenDefined('cart-items-component');
    if (typeof lista.fetchCartData === 'function') return lista.fetchCartData();
  }
  const odp = await fetch(`${routes().cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!odp.ok) throw new Error(`koszyk: HTTP ${odp.status}`);
  return odp.json();
}

class MotureoZestaw extends HTMLElement {
  #zajety = false;
  /** @type {Record<string, any>} */
  #teksty = {};

  connectedCallback() {
    try {
      this.#teksty = JSON.parse(this.querySelector('[data-zestaw-teksty]')?.textContent || '{}');
    } catch {
      this.#teksty = {};
    }
    this.querySelector('[data-zestaw-add]')?.addEventListener('click', () => this.dodaj());
    this.querySelector('[data-zestaw-edytuj]')?.addEventListener('click', (e) => this.#przelaczEdycje(e));
    // koszyk zmieniony gdziekolwiek (szuflada, inne przyciski): nowa wartość koszyka do zdania o dostawie
    this.#poZmianieKoszyka = (/** @type {any} */ e) => {
      e.promise
        ?.then(() => pobierzKoszyk())
        .then((/** @type {any} */ koszyk) => {
          if (typeof koszyk?.total_price === 'number') {
            this.dataset.koszyk = String(koszyk.total_price);
            this.#przelicz();
          }
        })
        .catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#poZmianieKoszyka);
    this.addEventListener('change', (e) => {
      const pole = /** @type {HTMLElement} */ (e.target);
      if (!pole.matches('[data-zestaw-pole]')) return;
      pole.closest('.motureo-zestaw__kafel')?.classList.toggle('motureo-zestaw__kafel--odznaczony', !(/** @type {HTMLInputElement} */ (pole)).checked);
      this.#przelicz();
    });
  }

  disconnectedCallback() {
    if (this.#poZmianieKoszyka) document.removeEventListener(StandardEvents.cartLinesUpdate, this.#poZmianieKoszyka);
  }

  /** @type {((e: any) => void) | null} */
  #poZmianieKoszyka = null;

  /** @param {number} centy */
  #kwota(centy) {
    return formatMoney(centy, this.dataset.format || '{{amount}}', this.dataset.waluta || 'EUR');
  }

  /** @returns {HTMLInputElement[]} */
  #zaznaczone() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-zestaw-pole]')]).filter((p) => p.checked);
  }

  #przelicz() {
    const pola = this.#zaznaczone();
    const p0 = Number(this.dataset.cenaGlowna) || 0;
    const suma = pola.reduce((s, p) => s + (Number(p.dataset.cena) || 0), p0);
    const ile = pola.length + 1;
    const etykieta = this.querySelector('[data-zestaw-etykieta]');
    const tekstCeny = this.#teksty.ceny?.[String(ile)];
    if (etykieta && tekstCeny) etykieta.textContent = tekstCeny;
    const sumaEl = this.querySelector('[data-zestaw-suma]');
    if (sumaEl) sumaEl.textContent = this.#kwota(suma);

    const dostawaEl = this.querySelector('[data-zestaw-dostawa]');
    if (!dostawaEl || this.dataset.krajDostawy !== 'true') return;
    const prog = Number(this.dataset.prog) || 0;
    const koszyk = Number(this.dataset.koszyk) || 0;
    const dostawa = Number(this.dataset.dostawa) || 0;
    dostawaEl.replaceChildren();
    if (koszyk >= prog) {
      dostawaEl.textContent = this.#teksty.koszyk || '';
    } else if (koszyk + p0 >= prog) {
      dostawaEl.textContent = this.#teksty.gratis || '';
    } else if (koszyk + suma >= prog) {
      if (dostawa > 0) {
        const plakietka = document.createElement('span');
        plakietka.className = 'motureo-zestaw__plakietka';
        // „−7,00 € от доставката”: oszczędność na dostawie, nie rabat od zestawu (przegląd Opusa r2, D4)
        plakietka.textContent = (this.#teksty.plakietka || '−__KWOTA__').replace('__KWOTA__', this.#kwota(dostawa));
        dostawaEl.append(plakietka, ' ');
      }
      dostawaEl.append(this.#teksty.oszczednosc || '');
    } else {
      dostawaEl.textContent = (this.#teksty.brakuje || '').replace('__KWOTA__', this.#kwota(prog - koszyk - suma));
    }
  }

  /** @param {Event} e */
  #przelaczEdycje(e) {
    const przycisk = /** @type {HTMLButtonElement} */ (e.currentTarget);
    const otwarte = przycisk.getAttribute('aria-expanded') === 'true';
    for (const kafel of this.querySelectorAll('[data-zestaw-dodatkowy]')) {
      // zwinięcie chowa tylko niezaznaczone dodatki: zaznaczony dodatek jest w zestawie, więc zostaje widoczny
      const zaznaczony = /** @type {HTMLInputElement | null} */ (kafel.querySelector('[data-zestaw-pole]'))?.checked;
      /** @type {HTMLElement} */ (kafel).hidden = otwarte && !zaznaczony;
    }
    przycisk.setAttribute('aria-expanded', String(!otwarte));
    przycisk.textContent = (otwarte ? this.#teksty.edytuj : this.#teksty.zwin) || przycisk.textContent;
  }

  /** @param {string} tekst */
  #status(tekst) {
    const el = this.querySelector('[data-zestaw-status]');
    if (el) el.textContent = tekst;
  }

  async dodaj() {
    const ids = [this.dataset.glowny, ...this.#zaznaczone().map((p) => p.dataset.id)]
      .map((x) => Number(x))
      .filter((x) => Number.isInteger(x) && x > 0);
    const przycisk = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-zestaw-add]'));
    if (!ids.length || this.#zajety) return;
    this.#zajety = true;
    if (przycisk) {
      przycisk.disabled = true;
      przycisk.setAttribute('aria-busy', 'true');
    }
    this.#status('');

    const sekcje = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const odroczone = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: odroczone.promise,
      })
    );

    const szczegoly = (/** @type {any} */ koszyk, /** @type {boolean} */ blad, /** @type {any} */ sekcjeHtml) => ({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(koszyk),
      detail: {
        items: koszyk.items,
        source: 'motureo-zestaw',
        sourceId: this.dataset.productId || 'motureo-zestaw',
        itemCount: blad ? 0 : ids.length,
        productId: this.dataset.productId,
        sections: sekcjeHtml,
        didError: blad,
      },
    });

    let bladOpis = '';
    try {
      const odp = await fetch(routes().cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })), sections: sekcje.join(',') }),
      });
      const dane = await odp.json();
      if (!odp.ok || dane.status) {
        // np. 422 „wyprzedane”: opis platformy mówi, której pozycji brakuje
        bladOpis = typeof dane.description === 'string' ? dane.description : '';
        throw new Error(dane.description || dane.message || `HTTP ${odp.status}`);
      }
      const koszyk = await pobierzKoszyk();
      odroczone.resolve(szczegoly(koszyk, false, dane.sections));
      this.#status(this.#teksty.dodano || '');
    } catch (blad) {
      console.error('[motureo-zestaw]', blad);
      this.dispatchEvent(
        new CartErrorEvent({ error: blad instanceof Error ? blad.message : 'Add to cart failed', code: 'INVALID' })
      );
      try {
        odroczone.resolve(szczegoly(await pobierzKoszyk(), true, undefined));
      } catch (e) {
        odroczone.reject(e);
      }
      this.#status([this.#teksty.blad || '', bladOpis].filter(Boolean).join(' '));
    } finally {
      this.#zajety = false;
      if (przycisk) {
        przycisk.disabled = false;
        przycisk.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('motureo-zestaw')) customElements.define('motureo-zestaw', MotureoZestaw);
